import { ScheduleSummaryPrioritizedLaunch } from 'src/Components/ScheduleSummary/ScheduleSummaryDetails/ScheduleSummaryStartDateRow'
import { formatDate } from 'src/Constants/formatDate'
import dayJs from 'src/Helpers/dayjs'
import { useHasPlan } from 'src/Hooks/useHasPlan'
import { usePrioritizedLaunchAddOn } from 'src/Hooks/usePrioritizedLaunchAddOn'
import { OrderItem, OrderItemType, OrderStatus } from 'src/Redux/artistOrderApi'
import { isOrderLocked } from 'src/Redux/artistOrderApi/isOrderLocked'
import { useGetSubscriptionQuery } from 'src/Redux/subscription-process/subscriptionApi'
import { CampaignStatusEnum, CampaignToSchedule } from 'src/Types/campaigns'

interface UseSpotifyPrioritizedLaunchParams {
  campaignDetails: CampaignToSchedule | null
  campaignId: number
  budget?: number | null
  startDate: string
  isManualMatching: boolean
  orderId?: number
  orderItems?: OrderItem[]
  orderStatus?: OrderStatus
  calculateCurrencyValue: (amount: number | undefined) => string
  onAfterMutation: () => Promise<void>
}

export interface UseSpotifyPrioritizedLaunchResult {
  prioritizedLaunch?: ScheduleSummaryPrioritizedLaunch
}

const getIsPrioritizedLaunchEligible = (
  isApprovedCampaign: boolean,
  isManualMatching: boolean,
  isSubscriptionResolved: boolean,
  hasPlan: boolean,
): boolean => {
  const isEligibleCampaign = isApprovedCampaign && !isManualMatching
  const isEligibleArtist = isSubscriptionResolved && !hasPlan

  return isEligibleCampaign && isEligibleArtist
}

export const useSpotifyPrioritizedLaunch = ({
  campaignDetails,
  campaignId,
  budget,
  startDate,
  isManualMatching,
  orderId,
  orderItems,
  orderStatus,
  calculateCurrencyValue,
  onAfterMutation,
}: UseSpotifyPrioritizedLaunchParams): UseSpotifyPrioritizedLaunchResult => {
  const { isSuccess: isSubscriptionResolved } = useGetSubscriptionQuery()
  const hasPlan = useHasPlan()

  const isEligible = getIsPrioritizedLaunchEligible(
    campaignDetails?.status === CampaignStatusEnum.APPROVED,
    isManualMatching,
    isSubscriptionResolved,
    hasPlan,
  )

  const { isEnabled, price, onChange } = usePrioritizedLaunchAddOn({
    orderId,
    orderItems,
    campaignId,
    isEligible,
    onAfterMutation,
    budget: budget ?? 0,
    addOnType: OrderItemType.SpotifyPrioritizedLaunch,
    isOrderMutable: !isOrderLocked(orderStatus),
  })

  return {
    prioritizedLaunch:
      isEligible || isEnabled
        ? {
            startDate: dayJs(startDate).format(formatDate.formatDateMonthFull),
            price: `+ ${calculateCurrencyValue(price)}`,
            isEnabled,
            onChange,
          }
        : void 0,
  }
}
